import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DailyBehaviourV2} from '@shared/api/models/query/patient-daily-snapshot.model';

/** Chooses the panel header label: 'patient' → "Behaviour log", 'carer' → "Observed behaviours". */
export type DailyBehaviourPanelVariant = 'patient' | 'carer';

/**
 * Case-insensitive substring → icon lookup for the row icon-avatar. Matched against
 * `behaviour.name` as delivered verbatim by the v2 payload (e.g. "Used laxatives",
 * "Purged") rather than a fixed enum, so this is a best-effort visual aid — any
 * unmatched name still renders with DEFAULT_BEHAVIOUR_ICON rather than nothing.
 */
const BEHAVIOUR_ICONS: [needle: string, icon: string][] = [
    ['laxative', 'bi-capsule'],
    ['diuretic', 'bi-droplet-half'],
    ['purg', 'bi-arrow-return-left'],
    ['binge', 'bi-cookie'],
    ['skip', 'bi-skip-forward-fill'],
    ['exercis', 'bi-person-walking'], // covers "Hours exercising" and legacy "Compulsive exercise"
];
const DEFAULT_BEHAVIOUR_ICON = 'bi-flag-fill';

/**
 * Daily behaviour log panel (UNIVAPLAT-2379 — daily check-ins v2; redesigned
 * UNIVAPLAT-2360 to match the reference design: a header with an audience
 * subtitle, and single-line rows — icon avatar, name, count badge, trend,
 * and a severity-coloured progress bar all on one line, in place of the
 * earlier severity-coloured pill background). Rendered in the left column
 * (col-left) at the same width as cdss-daily-checkin-card, below it.
 *
 * Bind [behaviours] with the flat `behaviours: DailyBehaviourV2[]` array from
 * the v2 daily statistics payload (UNIVAPLAT-2359 contract) plus [variant]:
 *   - populated array → one row per behaviour, label verbatim from the payload and the
 *     counter rendered exactly as stored (fractional values allowed — legacy exercise days);
 *     each row also renders a trend indicator from `behaviour.trend` — an up arrow when
 *     'up' (today's counter higher than yesterday's), a down arrow when 'down' (lower),
 *     a dash when 'same', and nothing when null (no comparable previous-day data). The
 *     icon is `aria-hidden` with a `.visually-hidden` text equivalent alongside it;
 *     each row also renders a progress bar from `behaviour.percentage` (0-100, already
 *     rounded/clamped server-side — UNIVAPLAT-2359/UNIVAPLAT-3031 contract), coloured by
 *     `behaviour.severity`, alongside the counter and trend, not in place of either. The
 *     bar sits inline after the trend icon on the same row and fills the remaining row
 *     width; it exposes `role="progressbar"` with
 *     `aria-valuenow`/`aria-valuemin`/`aria-valuemax` plus an `aria-label` naming the
 *     behaviour, matching the accessible-name pattern already used for the trend icon;
 *   - empty array     → "None today" rendered as a positive (green) result. This is a
 *     clinically meaningful state (no behaviours logged on a submitted day), NOT a blank;
 *   - null (unbound)  → nothing renders.
 *
 * The pre-v2 legacy [behaviourPanel]/[layout] snapshot mode was removed by the
 * UNIVAPLAT-2382 cleanup sub-task after the daily detail pages were rewired
 * onto the v2 payload (UNIVAPLAT-2380/2381).
 */
@Component({
    selector: 'cdss-daily-behaviour-panel',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './daily-behaviour-panel.component.html',
    styleUrl: './daily-behaviour-panel.component.scss'
})
export class DailyBehaviourPanelComponent {

    /** Flat behaviour list from the v2 daily payload. */
    @Input() behaviours: DailyBehaviourV2[] | null = null;

    /** Panel audience — drives the header label. */
    @Input() variant: DailyBehaviourPanelVariant = 'patient';

    get headerLabel(): string {
        return this.variant === 'carer' ? 'Observed behaviours' : 'Behaviour log';
    }

    /** Muted header subtitle naming who reported the data, alongside headerLabel. */
    get headerSubtitle(): string {
        return this.variant === 'carer' ? 'carer reported' : 'patient reported';
    }

    trackByBehaviourName(_index: number, item: DailyBehaviourV2): string {
        return item.name;
    }

    /** Best-effort icon-avatar class for a behaviour name — see BEHAVIOUR_ICONS. */
    iconFor(name: string): string {
        const lower = name?.toLowerCase() ?? '';
        return BEHAVIOUR_ICONS.find(([needle]) => lower.includes(needle))?.[1] ?? DEFAULT_BEHAVIOUR_ICON;
    }
}
